import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const SavingPlan = ({ userId }) => {
  const { t } = useTranslation();
  const [budget, setBudget] = useState(0);
  const [name, setName] = useState('');
  const [days, setDays] = useState(0);
  const [plan, setPlan] = useState(null);
  const [userPlans, setUserPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Fetch user's saved plans
  useEffect(() => {
    const fetchSavingPlans = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('https://trakersmallbusienss-3.onrender.com/api/auth/savingPlans', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserPlans(res.data.savingPlans);
      } catch (error) {
        console.error('Error fetching saving plans:', error);
      }
    };

    fetchSavingPlans();
  }, []);

  const createPlan = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        'https://trakersmallbusienss-3.onrender.com/api/auth/createSavingPlan',
        { userId, name, targetBudget: budget, days },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newPlan = res.data;
      setPlan(newPlan);

      // Add new plan to the table immediately
      setUserPlans((prev) => [...prev, newPlan]);

      // Show success alert
      Swal.fire({
        title: t('savingPlan.successTitle') || 'Success!',
        text: t('savingPlan.successMessage') || 'Saving plan created successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
      });

      // Optionally reset the form
      setName('');
      setBudget(0);
      setDays(0);

    } catch (error) {
      console.error('Error creating saving plan:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create saving plan.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const updateDay = async (day, action) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `https://trakersmallbusienss-3.onrender.com/api/auth/plan/${selectedPlan._id}/day/${day}`,

        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSelectedPlan((prev) => {
        const updated = prev.plan.map((p) =>
          p.day === day ? res.data.updatedDay : p
        );
        return { ...prev, plan: updated };
      });
    } catch (error) {
      console.error('Error updating day:', error);
    }
  };

  const loadPlanDetails = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`https://trakersmallbusienss-3.onrender.com/api/auth/plan/${planId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedPlanId(planId);
      setSelectedPlan(res.data);
    } catch (error) {
      console.error('Error loading plan details:', error);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center">💸 {t('savingPlan.title')}</h2>

      <div className='mb-5'>
        <label className="block mb-1 font-medium">🏦 {t('savingPlan.nameSaving')}</label>
        <input
          type="text"
          className="w-full p-2 border rounded"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('savingPlan.nameSavingPlaceholder')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block mb-1 font-medium">🎯 {t('savingPlan.targetBudget')}</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder={t('savingPlan.targetBudgetPlaceholder')}
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">📅 {t('savingPlan.numberOfDays')}</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            placeholder={t('savingPlan.numberOfDaysPlaceholder')}
          />
        </div>
      </div>

      <button
        onClick={createPlan}
        className="w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
      >
        {t('savingPlan.startButton')}
      </button>

      <div className="mt-10">
        <h3 className="text-xl font-semibold mb-4">📁 {t('savingPlan.savedPlans')}</h3>
        <table className="w-full table-fixed border border-gray-300">
          <thead>
            <tr className="bg-gray-100 text-left h-10 text-sm font-medium">
              <th className="w-1/2 px-4">{t('savingPlan.table.planName')}</th>
              <th className="w-1/2 px-4">{t('savingPlan.table.days')}</th>
            </tr>
          </thead>
          <tbody>
            {userPlans.map((plan) => (
              <tr
                key={plan._id}
                className="border-t h-12 text-left hover:bg-gray-50 cursor-pointer"
                onClick={() => loadPlanDetails(plan._id)}
              >
                <td className="px-4 text-blue-600 underline">{plan.name || 'Unnamed Plan'}</td>
                <td className="px-4">{plan.days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedPlan && (
        <div className="mt-10">
          <h3 className="text-xl font-semibold mb-4">📊 {t('savingPlan.yourPlanDetails')}</h3>
          <table className="w-full table-fixed border border-gray-300">
            <thead>
              <tr className="bg-gray-100 text-left h-12 text-sm font-medium">
                <th className="w-1/3 px-4">{t('savingPlan.table.day')}</th>
                <th className="w-1/3 px-4">{t('savingPlan.table.amount')}</th>
                <th className="w-1/3 px-4 text-center">{t('savingPlan.table.status')}</th>
              </tr>
            </thead>
            <tbody>
              {selectedPlan.plan.map((item) => {
                const saved = item.saved ?? { likes: 0, dislikes: 0 };

                return (
                  <tr
                    key={item.day}
                    className="border-t h-[80px] text-center text-base hover:bg-gray-50"
                  >
                    <td className="px-4 font-medium">{t('savingPlan.table.dayLabel', { day: item.day })}</td>
                    <td className="px-4 text-green-600 font-semibold">${item.amount}</td>
                    <td className="px-4">
                      <div className="flex justify-center items-center space-x-6 text-2xl">
                        <button
                          onClick={() => updateDay(item.day, 'like')}
                          className="flex items-center space-x-1 text-blue-600 hover:scale-110 transition"
                        >
                          <span>👍</span>
                          <span className="text-sm font-semibold">{saved.likes}</span>
                        </button>
                        <button
                          onClick={() => updateDay(item.day, 'dislike')}
                          className="flex items-center space-x-1 text-red-500 hover:scale-110 transition"
                        >
                          <span>👎</span>
                          <span className="text-sm font-semibold">{saved.dislikes}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SavingPlan;
